import { ZonedDateTime } from '@js-joda/core';
import { Box } from '@mui/material';
import React from 'react';
import {
  ArrayField,
  BooleanInput,
  Confirm,
  Create,
  Datagrid,
  DateField,
  DateTimeInput,
  Edit,
  Filter,
  Labeled,
  NumberField,
  ReferenceField,
  ResourceContextProvider,
  required,
  SaveButton,
  Show,
  ShowButton,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  useRecordContext,
} from 'react-admin';
import { useWatch } from 'react-hook-form';

import { AdminCohortDetailsFragment } from '../../graphql/types';
import { iso8601DateTime } from '../../utils/date';
import AutocompleteUserArrayInput from './components/AutocompleteUserArrayInput';
import {
  EligibilityWindow,
  EligibilityWindowView,
} from './components/EligibilityWindow';
import InfiniteList from './components/InfiniteList';
import {
  endAfter,
  formatUtcDateTimeInput,
  parseUtcDateTimeInput,
  parseZonedDateTime,
} from './components/utcDateTimeInput';
import { WriteOnlyDatagrid } from './components/WriteOnlyDatagrid';
import { WriteOnlyDeleteButton } from './components/WriteOnlyDeleteButton';
import { WriteOnlyEditButton } from './components/WriteOnlyEditButton';

/**
 * There are no currently existing patterns for confirmation on save
 * This should be pulled into a reusable component if this pattern occurs again somewhere else
 */
export const SaveWithConfirmButton: React.FC = () => {
  const record = useRecordContext<AdminCohortDetailsFragment>();
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const isConfirmed = React.useRef(false);
  const saveButtonRef = React.useRef<HTMLButtonElement>(null);
  const memberCount = record?.members.count ?? 0;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (memberCount === 0 || isConfirmed.current) {
      isConfirmed.current = false;
      return;
    }
    event.preventDefault();
    setIsConfirmOpen(true);
  };

  const handleConfirm = () => {
    setIsConfirmOpen(false);
    isConfirmed.current = true;
    saveButtonRef.current?.click();
  };

  return (
    <>
      <SaveButton ref={saveButtonRef} onClick={handleClick} />
      <Confirm
        isOpen={isConfirmOpen}
        title="Save cohort with members"
        content={`This cohort has ${memberCount} ${
          memberCount === 1 ? 'member' : 'members'
        }. Changing its dates changes their high-performance coaching eligibility. Save anyway?`}
        confirm="Save"
        onConfirm={handleConfirm}
        onClose={() => setIsConfirmOpen(false)}
      />
    </>
  );
};

const CohortToolbar = () => (
  <Toolbar>
    <SaveWithConfirmButton />
  </Toolbar>
);

const CohortFilters: React.FC = (props) => (
  <Filter {...props}>
    <TextInput label="Name" source="name" alwaysOn />
    <BooleanInput label="Include Archived" source="includeArchived" alwaysOn />
  </Filter>
);

/** Archived cohorts can't be edited or archived again, so hide those actions. */
const ActiveCohortOnly: React.FC<React.PropsWithChildren> = ({ children }) => {
  const record = useRecordContext<AdminCohortDetailsFragment>();
  if (!record || record.archivedAt) {
    return null;
  }
  return <>{children}</>;
};

export const ArchiveCohortButton: React.FC = () => (
  <ActiveCohortOnly>
    <WriteOnlyDeleteButton
      label="Archive"
      mutationMode="pessimistic"
      confirmTitle="Archive cohort"
      confirmContent="Are you sure you want to archive this cohort? Note that cohorts with members can only be archived after their end date."
    />
  </ActiveCohortOnly>
);

export const CohortList: React.FC = () => (
  // empty={false} keeps the filters (e.g. Include Archived) visible when
  // there are no active cohorts.
  <InfiniteList filters={<CohortFilters />} empty={false}>
    <WriteOnlyDatagrid bulkActionButtons={false}>
      <TextField source="name" sortable={false} />
      <DateField
        source="startDate"
        label="Start Date"
        showTime
        sortable={false}
      />
      <DateField source="endDate" label="End Date" showTime sortable={false} />
      <NumberField source="members.count" label="Members" sortable={false} />
      <NumberField source="coaches.count" label="Coaches" sortable={false} />
      <DateField
        source="archivedAt"
        label="Archived At"
        emptyText="—"
        sortable={false}
      />
      <ActiveCohortOnly>
        <WriteOnlyEditButton />
      </ActiveCohortOnly>
      <ShowButton />
    </WriteOnlyDatagrid>
  </InfiniteList>
);

const CohortMembers: React.FC = () => {
  const record = useRecordContext<AdminCohortDetailsFragment>();
  if (!record) {
    return null;
  }
  return (
    <ResourceContextProvider value="EligibleEmailAddress">
      <InfiniteList
        filter={{ cohortId: record.id }}
        disableSyncWithLocation
        storeKey={`cohort-${record.id}-members`}
        actions={false}
        title={false}
        empty={false}
      >
        <Datagrid bulkActionButtons={false} rowClick="show">
          <TextField source="email" sortable={false} />
          <ReferenceField
            source="client.id"
            reference="Client"
            label="Client"
            sortable={false}
          >
            <TextField source="name" />
          </ReferenceField>
          <ShowButton />
        </Datagrid>
      </InfiniteList>
    </ResourceContextProvider>
  );
};

const CohortShowActions: React.FC = () => (
  <TopToolbar>
    <ActiveCohortOnly>
      <WriteOnlyEditButton />
    </ActiveCohortOnly>
    <ArchiveCohortButton />
  </TopToolbar>
);

export const CohortShow: React.FC = () => (
  <Show actions={<CohortShowActions />}>
    <SimpleShowLayout>
      <TextField source="id" label="ID" />
      <TextField source="name" />
      <Labeled label="Eligibility Window">
        <EligibilityWindow startSource="startDate" endSource="endDate" />
      </Labeled>
      <DateField
        source="archivedAt"
        label="Archived At"
        showTime
        emptyText="—"
      />
      <ArrayField source="coaches.nodes" label="Coaches">
        <ResourceContextProvider value="User">
          <Datagrid
            bulkActionButtons={false}
            rowClick="show"
            empty={<>No coaches</>}
          >
            <TextField source="email" sortable={false} />
            <TextField source="firstName" label="First Name" sortable={false} />
            <TextField source="lastName" label="Last Name" sortable={false} />
          </Datagrid>
        </ResourceContextProvider>
      </ArrayField>
      <NumberField source="members.count" label="Member Count" />
      <Labeled label="Members" fullWidth>
        <CohortMembers />
      </Labeled>
    </SimpleShowLayout>
  </Show>
);

/** Live preview of the window the start/end inputs currently describe. */
export const CohortDatePreview: React.FC = () => {
  const start = parseZonedDateTime(useWatch({ name: 'startDate' }));
  const end = parseZonedDateTime(useWatch({ name: 'endDate' }));
  return (
    <Box sx={{ mb: 2 }}>
      <EligibilityWindowView start={start} end={end} />
    </Box>
  );
};

const CohortInputs: React.FC = () => (
  <>
    <TextInput source="name" validate={required()} />
    <DateTimeInput
      source="startDate"
      label="Start Date (UTC)"
      format={formatUtcDateTimeInput}
      parse={parseUtcDateTimeInput}
      validate={required()}
    />
    <DateTimeInput
      source="endDate"
      label="End Date (UTC)"
      format={formatUtcDateTimeInput}
      parse={parseUtcDateTimeInput}
      validate={[required(), endAfter('startDate')]}
    />
    <CohortDatePreview />
    <AutocompleteUserArrayInput
      source="coaches.ids"
      label="Coaches"
      filter={{ isCoach: true }}
    />
  </>
);

export const CohortEdit: React.FC = () => (
  <Edit mutationMode="pessimistic" redirect="show">
    <SimpleForm toolbar={<CohortToolbar />}>
      <TextInput source="id" InputProps={{ disabled: true }} />
      <CohortInputs />
    </SimpleForm>
  </Edit>
);

// Defaults live on the form, not the inputs: useWatch in CohortDatePreview
// doesn't see input-level defaultValues, so the preview would stay empty.
export const getCohortDefaultValues = () => ({
  startDate: iso8601DateTime(ZonedDateTime.now().withFixedOffsetZone()),
  endDate: iso8601DateTime(
    ZonedDateTime.now().plusMonths(3).withFixedOffsetZone(),
  ),
});

export const CohortCreate: React.FC = () => (
  <Create redirect="show">
    <SimpleForm defaultValues={getCohortDefaultValues}>
      <CohortInputs />
    </SimpleForm>
  </Create>
);
